/**
 * 목록 아래의 쪽 넘기기 — 받은 줄을 정렬한 뒤 그리는 것만 나눈다(`paging.ts`).
 *
 * 보던 쪽은 주소(`?page=`)에 둔다 — 상세를 열었다 뒤로 오면 그 쪽으로, 스크롤까지 돌아온다(`useListReturn`).
 * 쪽당 수는 브라우저에 기억한다(보기 방식과 같다) — 사람마다 편한 길이가 있고, 링크를 받은 사람의 것까지
 * 바꿀 까닭이 없다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { UrlQuery } from './urlState.js';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
  pageForRow,
  pageNumbers,
  parsePageParam,
  parsePageSize,
  type PageSlice,
} from './paging.js';

const SIZE_KEY = 'signageone.pageSize.';

function readSize(table: string): number {
  try {
    return parsePageSize(window.localStorage.getItem(SIZE_KEY + table));
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export interface Paging {
  /** 주소가 고른 쪽 — 끝을 넘을 수 있다(`paginate` 가 마지막 쪽으로 본다). */
  page: number;
  size: number;
  setPage: (page: number) => void;
  /** `firstRow` 는 지금 보이는 첫 줄의 차례 — 바꾼 뒤에도 그 줄이 든 쪽을 연다. */
  setSize: (size: number, firstRow: number) => void;
}

/**
 * 쪽 상태. `resetKey` 가 바뀌면(검색·거르기·정렬) 1쪽으로 돌아간다 — 거른 결과의 3쪽은 거르기 전의 3쪽과
 * 아무 관계가 없다. 처음 그릴 때는 돌리지 않는다: 상세에서 뒤로 온 목록은 주소의 쪽을 그대로 연다.
 */
export function usePaging(url: UrlQuery, table: string, resetKey: string): Paging {
  const page = parsePageParam(url.get('page'));
  const [size, setSizeState] = useState(() => readSize(table));
  const lastKey = useRef(resetKey);

  useEffect(() => {
    if (lastKey.current === resetKey) return;
    lastKey.current = resetKey;
    if (url.has('page')) url.set({ page: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 거르는 조건이 바뀔 때만 본다.
  }, [resetKey]);

  const go = (next: number): void => {
    url.set({ page: next > 1 ? String(next) : null });
    // 아래의 쪽 단추를 눌렀다 — 다음 쪽의 첫 줄부터 보이게. 콘솔은 문서가 아니라 본문(`main`)이 구른다.
    document.querySelector('main')?.scrollTo({ top: 0 });
  };

  return {
    page,
    size,
    setPage: go,
    setSize: (next, firstRow) => {
      try {
        window.localStorage.setItem(SIZE_KEY + table, String(next));
      } catch {
        // 기억하지 못해도 이번에는 적용된다.
      }
      setSizeState(next);
      url.set({
        page: pageForRow(firstRow, next) > 1 ? String(pageForRow(firstRow, next)) : null,
      });
    },
  };
}

/**
 * 쪽 번호·이전·다음과 쪽당 수.
 *
 * 가장 작은 쪽 하나에 다 들어가면 그리지 않는다 — 넘길 것도, 고를 것도 없다. 쪽이 하나뿐이어도 쪽당 수를
 * 키워 둔 사람에게는 고르개를 남긴다(다시 줄이는 길).
 */
export function Pager<T>({
  slice,
  paging,
  unit = '건',
}: {
  slice: PageSlice<T>;
  paging: Paging;
  unit?: string;
}): ReactNode {
  if (slice.total <= PAGE_SIZES[0]) return null;
  const go = (next: number): void => {
    if (next !== slice.page) paging.setPage(next);
  };
  return (
    <nav className="pager" aria-label="쪽 이동">
      <span className="pager-range">
        {slice.from.toLocaleString('ko-KR')}–{slice.to.toLocaleString('ko-KR')} / {slice.total.toLocaleString('ko-KR')}
        {unit}
      </span>
      {slice.pageCount > 1 ? (
        <div className="pager-pages">
          <button
            type="button"
            className="pager-btn"
            aria-label="이전 쪽"
            disabled={slice.page <= 1}
            onClick={() => go(slice.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          {pageNumbers(slice.page, slice.pageCount).map((cell, index) =>
            cell === 'gap' ? (
              <span key={`gap-${index}`} className="pager-gap" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={cell}
                type="button"
                className="pager-btn"
                aria-label={`${cell}쪽`}
                aria-current={cell === slice.page ? 'page' : undefined}
                onClick={() => go(cell)}
              >
                {cell}
              </button>
            ),
          )}
          <button
            type="button"
            className="pager-btn"
            aria-label="다음 쪽"
            disabled={slice.page >= slice.pageCount}
            onClick={() => go(slice.page + 1)}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      ) : null}
      <select
        className="input input-sm pager-size"
        aria-label="한 쪽에 보일 수"
        value={paging.size}
        onChange={(event) => paging.setSize(Number(event.target.value), slice.from)}
      >
        {PAGE_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}개씩
          </option>
        ))}
      </select>
    </nav>
  );
}
